import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { VideoCallProvider } from './context/VideoCallContext';
import { resolvePostLoginPath } from './utils/postLoginRedirect';

// Páginas existentes
import HomePage        from './pages/HomePage';
import FaqPage         from './pages/FaqPage';
import SupportPage     from './pages/SupportPage';
import LandingPatients from './pages/LandingPatients';
import LandingDoctors  from './pages/LandingDoctors';
import LoginPage       from './pages/auth/LoginPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage  from './pages/auth/ResetPasswordPage';
import RegisterPage    from './pages/auth/RegisterPage';
import WhatsappAccessPage from './pages/auth/WhatsappAccessPage';
import DashboardDoctor  from './pages/doctor/DashboardDoctor';
import DashboardPatient from './pages/patient/DashboardPatient';

// Admin
import DashboardAdmin from './pages/admin/DashboardAdmin';
import AgendaPage     from './pages/admin/AgendaPage';
import MedicalRecordsPage from './pages/admin/MedicalRecordsPage';
import useDoctorRegistrationOpen from './hooks/useDoctorRegistrationOpen';

// /medicos invita a registrarse como médico: solo se muestra con el registro de
// médicos abierto desde el panel admin. Cerrado, redirige al inicio para no
// romper enlaces viejos.
const DoctorsLandingRoute = () => {
  const { open, loading } = useDoctorRegistrationOpen();
  if (loading) return null;
  return open ? <LandingDoctors /> : <Navigate to="/" replace />;
};

// Enlace corto del SMS de cita sin pagar: /c/<id> abre esa cita en el panel
// del paciente, donde está el botón de pagar. No lleva credencial; si no hay
// sesión, ProtectedRoute manda a /login y de ahí vuelve a la cita.
export const AppointmentShortLink = () => {
  const { id } = useParams();
  if (!/^[a-f0-9]{24}$/i.test(id || '')) return <Navigate to="/" replace />;
  return <Navigate to={`/dashboard/patient?cita=${id}`} replace />;
};

// Componente para rutas protegidas
export const ProtectedRoute = ({ children, requiredRole }) => {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Cargando...</div>
      </div>
    );
  }
  // Se guarda la ubicación completa (pathname + search): un paciente que abre
  // un enlace del correo sin sesión debe volver a ese documento tras entrar,
  // no caer en el panel sin saber a qué iba.
  if (!isAuthenticated) return <Navigate to="/login" replace state={{ from: location }} />;
  // Una sesión limitada de WhatsApp no puede acceder al dashboard completo.
  if (user.scoped === true) return <Navigate to="/login" />;
  if (requiredRole && user.role !== requiredRole) {
    const map = { doctor: '/dashboard/doctor', patient: '/dashboard/patient', admin: '/dashboard/admin' };
    return <Navigate to={map[user.role] || '/'} />;
  }
  return children;
};

export function AppRoutes() {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  const map = { doctor: '/dashboard/doctor', patient: '/dashboard/patient', admin: '/dashboard/admin' };
  const userDashboard = user ? (map[user.role] || '/') : '/';
  // Una sesión limitada de WhatsApp no cuenta como sesión plena para los
  // redirects de /login y /register (evita bucles con el guard de ProtectedRoute).
  const hasFullSession = isAuthenticated && user?.scoped !== true;

  // Al iniciar sesión, isAuthenticated cambia y esta ruta redirige por su
  // cuenta, ganándole la carrera al navigate() de LoginPage. Por eso también
  // aquí se respeta el destino guardado; sin él, todo sigue igual que antes.
  const loginTarget = resolvePostLoginPath(location.state?.from, userDashboard);

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/faq"  element={<FaqPage />} />
      <Route path="/faqs" element={<FaqPage />} />
      <Route path="/soporte"  element={<SupportPage />} />
      <Route path="/contacto" element={<SupportPage />} />

      {/* Landing pages informativas (públicas, SEO) */}
      <Route path="/pacientes" element={<LandingPatients />} />
      <Route path="/medicos"   element={<DoctorsLandingRoute />} />

      <Route path="/login"    element={hasFullSession ? <Navigate to={loginTarget} replace /> : <LoginPage />} />
      {/* Recuperación de contraseña: forgot redirige con sesión plena como /login;
          restablecer NO, porque quien ya tiene sesión puede querer cambiarla. */}
      <Route path="/olvide-contrasena" element={hasFullSession ? <Navigate to={userDashboard} replace /> : <ForgotPasswordPage />} />
      <Route path="/restablecer-contrasena" element={<ResetPasswordPage />} />
      <Route path="/register" element={hasFullSession ? <Navigate to={userDashboard} /> : <RegisterPage />} />

      {/* Acceso directo vía magic link (público, sesión limitada) */}
      <Route path="/whatsapp-access/:token" element={<WhatsappAccessPage />} />
      {/* Alias corto para los SMS: el enlace se factura por caracteres, y
          '/r/' ahorra 14 respecto a '/whatsapp-access/'. Misma página. */}
      <Route path="/r/:token" element={<WhatsappAccessPage />} />
      <Route path="/c/:id" element={<AppointmentShortLink />} />

      <Route path="/dashboard/doctor"
        element={<ProtectedRoute requiredRole="doctor"><DashboardDoctor /></ProtectedRoute>} />

      <Route path="/dashboard/patient"
        element={<ProtectedRoute requiredRole="patient"><DashboardPatient /></ProtectedRoute>} />

      <Route path="/dashboard/admin"
        element={<ProtectedRoute requiredRole="admin"><DashboardAdmin /></ProtectedRoute>} />

      {/* iter-3 */}
      <Route path="/admin/agenda"
        element={<ProtectedRoute requiredRole="admin"><AgendaPage /></ProtectedRoute>} />

      {/* ITER-3: Agenda admin */}
      <Route
        path="/admin/agenda"
        element={
          <ProtectedRoute requiredRole="admin">
            <AgendaPage />
          </ProtectedRoute>
        }
      />

      {/* ITER-4: Historias Clínicas admin */}
      <Route
        path="/admin/medical-records"
        element={
          <ProtectedRoute requiredRole="admin">
            <MedicalRecordsPage />
          </ProtectedRoute>
        }
      />

      {/* Sub-rutas admin (preparadas para iter-1+) */}
      <Route
        path="/dashboard/admin/*"
        element={
          <ProtectedRoute requiredRole="admin">
            <DashboardAdmin />
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        {/* Envuelve TODAS las rutas: el timbre de videollamada debe poder
            llegar aunque el usuario esté en el chat o en otra pantalla. */}
        <VideoCallProvider>
          <AppRoutes />
        </VideoCallProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
